import React from 'react';
import { isPositionOnly, POSITION_ONLY_LABEL, POSITION_ONLY_TITLE } from '../../utils/positionOnly';

/**
 * Shared template-editor field row. Renders the flat-list field row markup
 * exactly (same classes, badges, svg, styles) so the flat view and the
 * region-grouped view (RegionList) show identical rows with the same controls:
 * enabled checkbox, label, type, req/library/zone badges, and +Zone/edit/delete.
 *
 * Context-specific controls (e.g. the RegionList unassign "×" button or the
 * "Move to…" select) are passed via `extraActions` and rendered at the end of
 * `.field-actions`.
 */
export default function FieldRow({
  field,
  isSelected,
  isEnabled,
  fieldZones = [],
  librarySelectedKeys,
  onSelect,
  onToggleEnabled,
  onPlaceZone,
  onEdit,
  onDelete,
  extraActions = null,
}) {
  const hasZones = fieldZones.length > 0;

  return (
    <div
      className={`field-item ${isSelected ? 'field-item-selected' : ''} ${!isEnabled ? 'field-item-disabled' : ''}`}
      onClick={onSelect}
    >
      <div className="field-info">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={(e) => {
            e.stopPropagation();
            onToggleEnabled(field.id, isEnabled);
          }}
          onClick={(e) => e.stopPropagation()}
          className="field-enabled-toggle"
          title={isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
        />
        <strong>{field.label || field.field_key}</strong>
        <span className="field-type">{field.value_type || 'text'}</span>
        {field.required && <span className="field-required-badge">req</span>}
        {librarySelectedKeys?.has(field.field_key) && <span className="field-library-badge">Field library</span>}
        {isPositionOnly(fieldZones) && (
          <span className="field-position-only-badge" title={POSITION_ONLY_TITLE} style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: '#e0e7ff', color: '#3730a3', fontWeight: 600 }}>{POSITION_ONLY_LABEL}</span>
        )}
        {hasZones ? (
          <span className="field-zone-count">{fieldZones.length} zone{fieldZones.length !== 1 ? 's' : ''}</span>
        ) : (
          <span className="field-no-zone-badge" style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>No zone</span>
        )}
      </div>
      <div className="field-actions">
        {!hasZones && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlaceZone(field.id, field.label || field.field_key);
            }}
            className="btn-icon"
            title="Draw a zone for this field"
            style={{ fontSize: '10px', color: '#8b5cf6' }}
          >
            +Zone
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(field);
          }}
          className="btn-icon"
          title="Edit field"
        >
          ✏️
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field, fieldZones);
          }}
          className="btn-icon btn-delete-field"
          title={hasZones ? `Clear zones (keeps field)` : `Delete field`}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
        {extraActions}
      </div>
    </div>
  );
}
